import { computed, watch } from 'vue';
import { useGlobalStore } from '@/features/Global/store';
import { sentrySessionId } from '@/features/Sentry/sentry';
import { Logger } from '@/services/logger/Logger';
import { ConsoleLogTransport } from '@/services/logger/transports/ConsoleLogTransport';
import { LokiLogTransport } from '@/services/logger/transports/LokiLogTransport';
import type { LoggerContext, LoggerTransport } from '@/services/logger/types';
import {
  LOG_LEVEL_NAME,
  LOGGER_MODULE,
  LOGGER_TRANSPORT,
} from '@/services/logger/types';
import { isDev, isTest } from '@/utils/environments/environments';
import { useBrokerStore } from '@/features/Broker/store';
import { useTariffStore } from '@/features/Tariffs/store';
import { useCurrentStep } from '@/utils/useCurrentRoute/useCurrentStep';

export interface UseLogger {
  debug: (...msg: unknown[]) => void;
  info: (...msg: unknown[]) => void;
  notice: (...msg: unknown[]) => void;
  warning: (...msg: unknown[]) => void;
  error: (...msg: unknown[]) => void;
  critical: (...msg: unknown[]) => void;
  alert: (...msg: unknown[]) => void;
  emergency: (...msg: unknown[]) => void;
}

const context = computed((): LoggerContext => {
  if (useGlobalStore().sparte === null) {
    return {};
  }

  const brokerStore = useBrokerStore();

  return {
    sparte: useGlobalStore().getSparte,
    brokerId: brokerStore.broker.data?.id ?? undefined,
    sentryId: sentrySessionId,
    berechnungsId: useTariffStore().calculationId || undefined,
    step: useCurrentStep().step.value,
    bedarfId: brokerStore.bedarfInformation.bedarfId,
  };
});

const transports = new Map<symbol, LoggerTransport>();

if (!isTest()) {
  if (isDev()) {
    transports.set(Symbol(LOGGER_TRANSPORT.CONSOLE), new ConsoleLogTransport());
  }

  transports.set(Symbol(LOGGER_TRANSPORT.LOKI), new LokiLogTransport());
}

let logger: Logger | undefined;

export const useLogger = (
  module: LOGGER_MODULE = LOGGER_MODULE.GENERIC,
): UseLogger => {
  if (logger === undefined) {
    logger = new Logger(LOG_LEVEL_NAME.DEBUG, context.value, transports);
  }

  // watch for context related changes
  watch(
    () => context.value,
    () => {
      logger?.updateContext(context.value);
    },
    {
      deep: true,
    },
  );

  const debug = (...msg: unknown[]): void => {
    logger?.log(LOG_LEVEL_NAME.DEBUG, module, ...msg);
  };

  const info = (...msg: unknown[]): void => {
    logger?.log(LOG_LEVEL_NAME.INFO, module, ...msg);
  };

  const notice = (...msg: unknown[]): void => {
    logger?.log(LOG_LEVEL_NAME.NOTICE, module, ...msg);
  };

  const warning = (...msg: unknown[]): void => {
    logger?.log(LOG_LEVEL_NAME.WARNING, module, ...msg);
  };

  const error = (...msg: unknown[]): void => {
    logger?.log(LOG_LEVEL_NAME.ERROR, module, ...msg);
  };

  const critical = (...msg: unknown[]): void => {
    logger?.log(LOG_LEVEL_NAME.CRITICAL, module, ...msg);
  };

  const alert = (...msg: unknown[]): void => {
    logger?.log(LOG_LEVEL_NAME.ALERT, module, ...msg);
  };

  const emergency = (...msg: unknown[]): void => {
    logger?.log(LOG_LEVEL_NAME.EMERGENCY, module, ...msg);
  };

  return {
    debug,
    info,
    notice,
    warning,
    error,
    critical,
    alert,
    emergency,
  };
};
